import * as React from "react";
import {setOpenFiles, StateOfAllTables} from "../slices/tableSlice";
import {useDispatch, useSelector} from "react-redux";
import {Matrix2d, matrix2dToTable} from "../misc/matrix2d";
import "./LoadDataButton.css"
import {ConfigContext} from "../../../../main/context/ConfigContext";
import {
    CompanyConfiguration,
    GenericXlsConfigurationOverride,
    SplitterOptions as SplitterOpts
} from "../../../../../generated";
import {configStorage} from "../../../../../ConfigStorage";
import {
    changeActiveMode,
    getActiveConfig,
    getActiveMode,
    getSplitterOptions, setSaveToConfig,
    setSplitterOptions,
    AppSplitterOptions
} from "../slices/ruleSlice";
import InputFormatEnum = GenericXlsConfigurationOverride.InputFormatEnum;
import {Checkbox, Input, Label} from "semantic-ui-react";


export interface LoadDataButtonProps {
    onFilesSelected?: (files: File[]) => void;
}

export function LoadDataButton(props: React.PropsWithChildren<LoadDataButtonProps>) {
    const dispatch = useDispatch()

    const [isLoading, setLoading] = React.useState(false);
    const [skipHiddenRows, setSkipHiddenRows] = React.useState(false)
    const [separatorChar, setSeparatorChar] = React.useState(";")
    const [fileEncoding, setFileEncoding] = React.useState("UTF-8")
    const [evaluateFomulas, setEvaluateFormulas] = React.useState(false)

    const activeMode = useSelector(getActiveMode)
    const splitterOptions = useSelector(getSplitterOptions)

    // The fusion mode is Google-OCR based and offers exactly the same settings as GOOGLE_OCR_PDF_v2
    const isGoogleOcrBasedMode =
        activeMode === InputFormatEnum.GOOGLEOCRPDFV2 ||
        activeMode === InputFormatEnum.GOOGLEOCRPDFV2FUSIONWITHTESSERACT;

    const isAnyOcrMode = isGoogleOcrBasedMode || activeMode === InputFormatEnum.OCRPDFV2;

    const persistSoon = React.useCallback(
        () => setTimeout(() => dispatch(setSaveToConfig(true)), 1000),
        [dispatch]
    );

    // console.log(`Active mode is  ${activeMode}  is  ${activeMode === InputFormatEnum.XLS}`)

    function toggleSingleSplitterOption(
        option: "segmentDocument" |
            "useElasticPlacement" |
            "squashBuffer" |
            "disableHeaderRemoval" |
            "aggressiveLineMerging" |
            "includeEmptyLines" |
            "splitOnOversizedWhitespaces" |
            "extractHorizontalLines" |
            "shallConvertPdfToImagesBeforeExtraction" |
            "useAggressivePageDeduplication" |
            "useCorrectCalculationOfYCoordinate",
        event = null
    ) {

        const current: AppSplitterOptions = {
            segmentDocuments: false,
            useElasticCellPlacement: false,
            squashBuffer: false,
            disableHeaderRemoval: false,
            aggressiveLineMerging: false,
            insertEmptyLines: false,
            splitOnOversizedWhitespaces: false,
            extractHorizontalLines: false,
            shallConvertPdfToImagesBeforeExtraction: false,
            useAggressivePageDeduplication: false,
            changeRotationBeforeExtraction: splitterOptions?.changeRotationBeforeExtraction,
            ...splitterOptions,
        };
        const next: AppSplitterOptions = { ...current };

        switch (option) {
            case "segmentDocument":
                next.segmentDocuments = !current.segmentDocuments;
                break;
            case "useElasticPlacement":
                next.useElasticCellPlacement = !current.useElasticCellPlacement;
                break;
            case "squashBuffer":
                next.squashBuffer = !current.squashBuffer;
                break;
            case "disableHeaderRemoval":
                next.disableHeaderRemoval = !current.disableHeaderRemoval;
                break;
            case "aggressiveLineMerging":
                next.aggressiveLineMerging = !current.aggressiveLineMerging;
                break;
            case "includeEmptyLines":
                next.insertEmptyLines = !current.insertEmptyLines;
                break;
            case "splitOnOversizedWhitespaces":
                next.splitOnOversizedWhitespaces = !current.splitOnOversizedWhitespaces;
                break;
            case "extractHorizontalLines":
                next.extractHorizontalLines = !current.extractHorizontalLines;
                break;
            case "shallConvertPdfToImagesBeforeExtraction":
                next.shallConvertPdfToImagesBeforeExtraction = !current.shallConvertPdfToImagesBeforeExtraction;
                break;
            case "useAggressivePageDeduplication":
                next.useAggressivePageDeduplication = !current.useAggressivePageDeduplication;
                break;
            case "useCorrectCalculationOfYCoordinate":
                next.useCorrectCalculationOfYCoordinate = !current.useCorrectCalculationOfYCoordinate;
            default:
        }
        dispatch(setSplitterOptions(next));
        persistSoon();
    }


    const selectData = async (evt: React.ChangeEvent<HTMLInputElement>) => {
        setLoading(true);
        try {
            const uploadedFiles: File[] = Array.from(evt.target?.files ?? []);
            if (uploadedFiles.length === 0) {
                return;
            }

            props.onFilesSelected?.(uploadedFiles);
            // Build query string using the SAME param names as the original (no server assumptions)
            const params = new URLSearchParams({
                pdfExtractionMode: String(activeMode),
                segmentDocument: String(!!splitterOptions?.segmentDocuments),
                elasticPlacment: String(!!splitterOptions?.useElasticCellPlacement), // keep original param spelling
                squashBuffer: String(!!splitterOptions?.squashBuffer),
                disableHeaderRemoval: String(!!splitterOptions?.disableHeaderRemoval),
                aggressiveLineMerging: String(!!splitterOptions?.aggressiveLineMerging),
                insertEmptyLine: String(!!splitterOptions?.insertEmptyLines),
                splitOnOversizedWhitespaces: String(!!splitterOptions?.splitOnOversizedWhitespaces),
                skipHiddenRows: String(!!skipHiddenRows),
                separatorChar: separatorChar ?? ";",
                fileEncoding: fileEncoding ?? "UTF-8",
                extractHorizontalLines: String(!!splitterOptions?.extractHorizontalLines),
                evaluateExcelFormulas: String(!!evaluateFomulas),
                shallConvertPdfToImagesBeforeExtraction: String(
                    !!splitterOptions?.shallConvertPdfToImagesBeforeExtraction
                ),
                changeRotationBeforeExtraction: splitterOptions?.changeRotationBeforeExtraction ?? "",
                useAggressivePageDeduplication: String(!!splitterOptions?.useAggressivePageDeduplication),
                useCorrectCalculationOfYCoordinate: String(!!splitterOptions?.useCorrectCalculationOfYCoordinate)
            });

            const url = `/converter/extractMatrix2d/?${params.toString()}`;

            const results = await Promise.all(
                uploadedFiles.map(async (file) => {
                    const data = new FormData();
                    data.append("file", file);

                    const res = await fetch(url, { method: "POST", body: data });
                    if (!res.ok) {
                        const text = await res.text().catch(() => "");
                        throw new Error(`Upload failed for "${file.name}": ${res.status} ${res.statusText} ${text}`);
                    }
                    const responseData: Matrix2d = await res.json();
                    return matrix2dToTable(responseData, file.name ?? "");
                })
            );

            const tableResponse: StateOfAllTables = {
                openFiles: results,
                activeFile: results.length ? 0 : undefined,
            };

            dispatch(setOpenFiles(tableResponse));
        } catch (err) {
            console.error(err);
            alert(err instanceof Error ? err.message : "Unexpected error during upload");
        } finally {
            setLoading(false);
            if (evt.target) evt.target.value = ""; // allow re-selecting the same file
        }
    };

    if (isLoading) {
        return <span role="status" aria-live="polite">Bitte warten, wird geladen...</span>;
    }


    return <form className={"loadDataOptions"} aria-busy={isLoading}>
        <div>
            <Label as="label" htmlFor="pdfExtractionMode">Modus</Label>
            <select
                name="pdfExtractionMode"
                id="pdfExtractionMode"
                onChange={(e) => {
                    dispatch(changeActiveMode(e.target.value as InputFormatEnum));
                    persistSoon();
                }}
                value={activeMode ?? InputFormatEnum.PDFV2}
            >
                <option value={InputFormatEnum.PDFV2}>PDF_v2</option>
                <option value={InputFormatEnum.OCRPDFV2}>OCR_PDF_V2</option>
                <option value={InputFormatEnum.GOOGLEOCRPDFV2}>GOOGLE_OCR_PDF_V2</option>
                <option value={InputFormatEnum.GOOGLEOCRPDFV2FUSIONWITHTESSERACT}>GOOGLE_OCR_PDF_V2_WITH_TESSERACT_FUSION</option>
                <option value={InputFormatEnum.XLS}>XLS</option>
                <option value={InputFormatEnum.CSV}>CSV</option>
                <option value={InputFormatEnum.DOCX}>DOCX</option>
                <option value={InputFormatEnum.EDI}>EDI</option>
                <option value={InputFormatEnum.PDF}>PDF</option>
                <option value={InputFormatEnum.XML}>XML</option>
                <option value={InputFormatEnum.HTML}>HTML</option>
                <option value={InputFormatEnum.HTMLV2}>HTML_v2</option>
                <option value={InputFormatEnum.FORTRAS}>FORTRAS</option>
                <option value={InputFormatEnum.EML}>EML</option>
                <option value={InputFormatEnum.JSONMAIL}>JSON_MAIL</option>
                <option value={InputFormatEnum.JSON}>JSON</option>
            </select>
        </div>
        <div>
            <Checkbox
                id="segmentDocument"
                name="segmentDocument"
                label='Segment document'
                checked={!!splitterOptions?.segmentDocuments}
                onChange={() => toggleSingleSplitterOption("segmentDocument")}/>
        </div>
        <div>
            <Checkbox
                id="useElasticPlacement"
                name="useElasticPlacement"
                label='Use elastic placement'
                checked={!!splitterOptions?.useElasticCellPlacement}
                onChange={() => toggleSingleSplitterOption("useElasticPlacement")}/>
        </div>
        <div>
            <Checkbox
                id="squashBuffer"
                name="squashBuffer"
                label='Should squash buffer'
                checked={!!splitterOptions?.squashBuffer}
                onChange={() => toggleSingleSplitterOption("squashBuffer")}/>
        </div>
        <div>
            <Checkbox
                id="disableHeaderRemoval"
                name="disableHeaderRemoval"
                label='Disable Header and Footer Removal'
                checked={!!splitterOptions?.disableHeaderRemoval}
                onChange={() => toggleSingleSplitterOption("disableHeaderRemoval")}/>
        </div>
        <div>
            <Checkbox
                id="aggressiveLineMerging"
                name="aggressiveLineMerging"
                label='Enable aggressive line merging'
                checked={!!splitterOptions?.aggressiveLineMerging}
                onChange={() => toggleSingleSplitterOption("aggressiveLineMerging")}/>
        </div>
        <div>
            <Checkbox
                id="includeEmptyLines"
                name="includeEmptyLines"
                label='Insert Empty Lines'
                checked={!!splitterOptions?.insertEmptyLines}
                onChange={() => toggleSingleSplitterOption("includeEmptyLines")}/>
        </div>
        <div>
            <Checkbox
                id="splitOnOversizedWhitespaces"
                name="splitOnOversizedWhitespaces"
                label='Force split on oversized whitespace'
                checked={!!splitterOptions?.splitOnOversizedWhitespaces}
                onChange={() => toggleSingleSplitterOption("splitOnOversizedWhitespaces")}/>
        </div>

        {isAnyOcrMode ? (
            <div>
                <Checkbox
                    id="shallConvertPdfToImagesBeforeExtraction"
                    name="shallConvertPdfToImagesBeforeExtraction"
                    label="Convert PDF to Image before doing the OCR"
                    checked={!!splitterOptions?.shallConvertPdfToImagesBeforeExtraction}
                    onChange={() => toggleSingleSplitterOption("shallConvertPdfToImagesBeforeExtraction")}
                />
            </div>
        ) : null}

        {isAnyOcrMode ?
            <div>
                <label htmlFor="changeRotationBeforeExtraction">Rotation</label>
                <select
                    name="changeRotationBeforeExtraction"
                    id="changeRotationBeforeExtraction"
                    onChange={(e) => {
                        const value =
                            e.target.value === ""
                                ? null
                                : (e.target.value as SplitterOpts.ChangeRotationBeforeExtractionEnum);

                        // Dispatch a **partial** update; reducer merges it
                        dispatch(setSplitterOptions({ changeRotationBeforeExtraction: value } as any));
                        persistSoon();
                    }}
                    value={splitterOptions?.changeRotationBeforeExtraction ?? ""}
                >
                    <option value="">No Rotation</option>
                    <option value={SplitterOpts.ChangeRotationBeforeExtractionEnum.PLUS90DEG}>90 Deg Clockwise</option>
                    <option value={SplitterOpts.ChangeRotationBeforeExtractionEnum.PLUS180DEG}>180 DEG</option>
                    <option value={SplitterOpts.ChangeRotationBeforeExtractionEnum.MINUS90DEG}>90 Deg Counter Clockwise</option>
                </select>
            </div> : null
        }

        {activeMode === InputFormatEnum.XLS ?
            <div>
                <Checkbox
                    id="skipHiddenRows"
                    name="skipHiddenRows"
                    label="Skip hidden rows [Excel files only]"
                    checked={skipHiddenRows}
                    onChange={(_, d) => setSkipHiddenRows(!!d.checked)}
                />
            </div> : null
        }
        {activeMode === InputFormatEnum.XLS ?
            <div>
                <Checkbox
                    id="evaluateFomulas"
                    name="evaluateFomulas"
                    label="Evaluate formulas [Excel files only]"
                    checked={evaluateFomulas}
                    onChange={(_, d) => setEvaluateFormulas(!!d.checked)}
                />
            </div> : null
        }
        {activeMode === InputFormatEnum.CSV ?
            <div>
                <label htmlFor="separatorChar">Field Separator Character [CSV files only, defaults to ;]</label>
                <Input
                    id="separatorChar"
                    name="separatorChar"
                    value={separatorChar}
                    onChange={(_, data) => setSeparatorChar(String(data.value ?? ";"))}
                />
            </div> : null
        }
        {activeMode === InputFormatEnum.CSV ?
            <div>
                <label htmlFor="fileEncoding">File Encoding [CSV files only, defaults to UTF-8]</label>
                <Input
                    id="fileEncoding"
                    name="fileEncoding"
                    value={fileEncoding}
                    onChange={(_, data) => setFileEncoding(String(data.value ?? "UTF-8"))}
                />
            </div> : null
        }
        {
            isGoogleOcrBasedMode ?
                <div>
                    <Checkbox
                        id="extractHorizontalLines"
                        name="extractHorizontalLines"
                        label="Extract horizontal lines from images/pdfs [Currently only available with Google OCR]"
                        checked={!!splitterOptions?.extractHorizontalLines}
                        onChange={() => toggleSingleSplitterOption("extractHorizontalLines")}
                    />
                    <br/>
                    <Checkbox
                        id="useAggressivePageDeduplication"
                        name="useAggressivePageDeduplication"
                        label="Aggressive Page Deduplication"
                        checked={!!splitterOptions?.useAggressivePageDeduplication}
                        onChange={() => toggleSingleSplitterOption("useAggressivePageDeduplication")}
                    />
                </div>
                : null
        }
        {
            (isGoogleOcrBasedMode || activeMode === InputFormatEnum.PDFV2 || activeMode === InputFormatEnum.JSONMAIL || activeMode === undefined ) ?
                <div>
                    <Checkbox
                        id="useCorrectCalculationOfYCoordinate"
                        name="useCorrectCalculationOfYCoordinate"
                        label="Use correct calculation of y-Coordinate. This reduces the line height when text would overlap otherwise."
                        checked={!!splitterOptions?.useCorrectCalculationOfYCoordinate}
                        onChange={() => toggleSingleSplitterOption("useCorrectCalculationOfYCoordinate")}
                    />
                </div>
                : null
        }
        <div>
            <Label as="label" htmlFor="fileInput">Choose File(s)</Label>
            <Input
                id="fileInput"
                type={"file"}
                accept=".xls,.xlsx,.csv,.pdf,.edi,.html,.xml,.eml,.jsonmail"
                onChange={selectData}
                multiple
                disabled={isLoading}
            />
        </div>
    </form>
}